import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { orderKeys } from '@/features/orders/hooks/queryKeys';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { CreateLabelBody, Label, LabelSuccess, UpdateLabelBody } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const LABELS_PATH = '/api/restaurants/{restaurantId}/labels';
const LABEL_PATH = '/api/restaurants/{restaurantId}/labels/{labelId}';

export const labelKey = (restaurantId: string) => ['labels', restaurantId] as const;

export function useLabels() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: labelKey(restaurantId),
    queryFn: async () =>
      unwrapResult<Label[]>(
        await openapiClient.GET(LABELS_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

function useLabelMutationInvalidation() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: labelKey(restaurantId) });
    void queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
  };
}

export function useCreateLabel() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = useLabelMutationInvalidation();
  return useMutation({
    mutationFn: async (body: CreateLabelBody) =>
      unwrapResult<Label>(
        await openapiClient.POST(LABELS_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateLabel() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = useLabelMutationInvalidation();
  return useMutation({
    mutationFn: async ({ labelId, data }: { labelId: string; data: UpdateLabelBody }) =>
      unwrapResult<Label>(
        await openapiClient.PATCH(LABEL_PATH, {
          params: { path: { restaurantId, labelId } },
          body: data,
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteLabel() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const invalidate = useLabelMutationInvalidation();
  return useMutation({
    mutationFn: async (labelId: string) =>
      unwrapResult<LabelSuccess>(
        await openapiClient.DELETE(LABEL_PATH, { params: { path: { restaurantId, labelId } } }),
      ),
    onSuccess: invalidate,
  });
}
